import { useMemo, useState } from 'react';
import type { Round } from './logic';
import { scorePercent } from './logic';

interface Props<T> {
  rounds: Round<T>[];
  keyOf: (item: T) => string;
  renderPrompt: (round: Round<T>) => React.ReactNode;
  renderChoice: (item: T) => React.ReactNode;
  explain: (item: T) => string;
  onFinish: (score: number) => void;
}

/**
 * Shared engine for every mini-game: one question at a time, gentle feedback, no timers, no losing
 * streaks — children can retry wrong answers. Score = % correct on the first try.
 */
export function GameRunner<T>({ rounds, keyOf, renderPrompt, renderChoice, explain, onFinish }: Props<T>) {
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState<string | null>(null);
  const [wrong, setWrong] = useState<Set<string>>(new Set());
  const [firstTry, setFirstTry] = useState(0);
  const [missedThisRound, setMissed] = useState(false);
  const [done, setDone] = useState(false);
  const round = rounds[index];
  const total = rounds.length;
  const score = useMemo(() => scorePercent(firstTry, total), [firstTry, total]);

  if (!round) return <p>No questions in this game yet.</p>;

  if (done) {
    return (
      <div className="game stack" aria-live="polite">
        <p className="game-prompt" aria-hidden="true">
          🎉
        </p>
        <h2>
          <span lang="yo" className="yo">
            Ẹ kú iṣẹ́!
          </span>{' '}
          Well done!
        </h2>
        <p>
          You got {firstTry} of {total} on the first try ({score}%).
        </p>
      </div>
    );
  }

  const correct = picked !== null && picked === keyOf(round.answer);

  const choose = (item: T) => {
    const k = keyOf(item);
    if (correct) return;
    setPicked(k);
    if (k === keyOf(round.answer)) {
      if (!missedThisRound) setFirstTry((n) => n + 1);
    } else {
      setMissed(true);
      setWrong((w) => new Set(w).add(k));
    }
  };

  const next = () => {
    if (index + 1 >= total) {
      setDone(true);
      onFinish(scorePercent(firstTry, total));
      return;
    }
    setIndex(index + 1);
    setPicked(null);
    setWrong(new Set());
    setMissed(false);
  };

  return (
    <div className="game">
      <div className="progress-dots" aria-label={`Question ${index + 1} of ${total}`}>
        {rounds.map((_, i) => (
          <span key={i} className={i < index ? 'done' : i === index ? 'now' : ''} />
        ))}
      </div>
      {renderPrompt(round)}
      <div className="game-choices" role="group" aria-label="Choose an answer">
        {round.choices.map((c) => {
          const k = keyOf(c);
          const cls = correct && k === keyOf(round.answer) ? 'correct' : wrong.has(k) ? 'wrong' : '';
          return (
            <button key={k} type="button" className={`choice ${cls}`} lang="yo" onClick={() => choose(c)} aria-pressed={picked === k} disabled={wrong.has(k)}>
              {renderChoice(c)}
            </button>
          );
        })}
      </div>
      <div aria-live="polite" style={{ minHeight: 60 }}>
        {picked !== null &&
          (correct ? (
            <p>
              ✅ <strong lang="yo" className="yo">Ó dára!</strong> {explain(round.answer)}
            </p>
          ) : (
            <p>Not quite — try another one. 🙂</p>
          ))}
      </div>
      {correct && (
        <button className="btn" onClick={next} autoFocus>
          {index + 1 >= total ? 'Finish' : 'Next'}
        </button>
      )}
    </div>
  );
}
